import React, { useEffect, useState } from 'react';

// Accept new prop: availableWordlists
const ConfigurationPanel = ({
  scanConfig,
  crackConfig,
  onScanConfigChange,
  onCrackConfigChange,
  onStartProcess,
  isProcessing,
  canProcess,
  isHacked,
  selectedPort,
  availableWordlists = [], // Accept the list of wordlists, default to empty array
  onSelectPort
}) => {
  const [activeTab, setActiveTab] = useState('port');
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualPort, setManualPort] = useState('');
  const [manualPassword, setManualPassword] = useState('');

  useEffect(() => {
    if (selectedPort && !isProcessing && !isHacked) {
      setActiveTab('password');
    } else if (!selectedPort) {
      setActiveTab('port');
    }
  }, [selectedPort, isProcessing, isHacked]);

  // --- Input Handlers ---
  const handleThreadsChange = (e, type) => {
    const value = parseInt(e.target.value, 10);
    if (type === 'scan') {
      onScanConfigChange({ threads: value });
    } else {
      onCrackConfigChange({ threads: value });
    }
  };

  const handleDelayChange = (e) => { 
    onCrackConfigChange({ delay: parseInt(e.target.value, 10) || 50 }); 
  };

  const handleWordlistChange = (e) => { 
    const value = e.target.value;
    if (value === 'manual') {
      setShowManualInput(true);
      onCrackConfigChange({ wordlist: 'manual' });
    } else {
      setShowManualInput(false);
      onCrackConfigChange({ wordlist: value });
    }
  };

  const handleScanModeChange = (e) => { 
    onScanConfigChange({ scanMode: e.target.value }); 
  };

  const handleTimeoutChange = (e) => { 
    onScanConfigChange({ timeout: parseInt(e.target.value, 10) || 50 }); 
  };

  const handleManualSubmit = () => {
    if (manualPort && !isNaN(manualPort)) {
      const port = parseInt(manualPort, 10);
      onCrackConfigChange({ 
        wordlist: 'manual', 
        manualPort: port,
        manualPassword: manualPassword || undefined
      });
      onSelectPort(port);
      setShowManualInput(false);
    }
  };

  // --- Button Text/Icon ---
  const getButtonText = () => {
    if (isProcessing) return "Processing...";
    if (isHacked) return "Access Granted";
    if (selectedPort) return `Crack Port ${selectedPort}`;
    return "Start Rcon crack";
  };

  const getButtonIcon = () => {
    if (isProcessing) return "fa-spinner fa-spin";
    if (isHacked) return "fa-check-circle";
    if (selectedPort) return "fa-key";
    return "fa-satellite-dish";
  };

  // Disable inputs/selects if processing or hacked
  const isDisabled = isProcessing || isHacked;

  return (
    <div className="lg:col-span-1 transform-gpu transition-medium hover:scale-[1.01]">
      <div className={`bg-dark-600 rounded-2xl p-6 h-full flex flex-col border border-dark-400 relative overflow-hidden ${
           selectedPort && !isHacked ? 'gradient-bg-purple hover:border-accent-purple/30' : 'gradient-bg-blue hover:border-accent-blue/30'
      }`}>
        {/* Background elements */}
        <div className={`absolute -right-10 -top-10 w-32 h-32 ${selectedPort && !isHacked ? 'bg-accent-purple' : 'bg-accent-blue'} opacity-5 rounded-full blur-xl transition-colors duration-300`}></div>
        <div className={`absolute -left-10 -bottom-10 w-32 h-32 ${selectedPort && !isHacked ? 'bg-accent-pink' : 'bg-accent-green'} opacity-5 rounded-full blur-xl transition-colors duration-300`}></div>

        {/* Tabs */}
        <div className="flex mb-4 border-b border-dark-400 relative z-10">
          <button
            className={`pb-2 px-4 text-sm font-medium transition-medium ${
              activeTab === 'port'
                ? 'text-accent-blue border-b-2 border-accent-blue'
                : 'text-gray-400 hover:text-white disabled:text-gray-600 disabled:cursor-not-allowed'
            }`}
            onClick={() => setActiveTab('port')}
             disabled={isDisabled}
          >
            <i className="fas fa-network-wired mr-2"></i>
            Scanner Config
          </button>
          <button
            className={`pb-2 px-4 text-sm font-medium transition-medium ${
              activeTab === 'password'
                ? 'text-accent-purple border-b-2 border-accent-purple'
                : 'text-gray-400 hover:text-white disabled:text-gray-600 disabled:cursor-not-allowed'
            }`}
            onClick={() => setActiveTab('password')}
            disabled={isDisabled}
          >
            <i className="fas fa-key mr-2"></i>
            Cracker Config
          </button>
        </div>

        {/* Configuration Settings Area */}
        <div className="flex-1 space-y-5 relative z-10">
          {activeTab === 'port' ? (
            // --- Port Scanner Settings ---
            <>
              {/* Scan Mode Select */}
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                <label className="block text-xs text-gray-400 mb-2 group">
                  Scan Mode
                  <span className="ml-2 text-accent-blue opacity-0 group-hover:opacity-100 transition-medium">
                    Defines port range
                  </span>
                </label>
                <select
                    className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-blue/50 hover:border-dark-300 transition-medium appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                    value={scanConfig.scanMode || 'FULL'} // Default to FULL if undefined
                    onChange={handleScanModeChange}
                    disabled={isDisabled || !!selectedPort} // Disable if port is selected (means scan is done or skipped)
                >
                    <option value="FAST">Fast (Common: ~25700-30000)</option>
                    <option value="MIDDLE">Middle (Wider: ~10000-40000)</option>
                    <option value="FULL">Full (All: 1024-65535)</option>
                </select>
              </div>

              {/* Scan Threads Range */}
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                <label className="block text-xs text-gray-400 mb-2 group">
                  Threads (Scan)
                  <span className="ml-2 text-accent-blue opacity-0 group-hover:opacity-100 transition-medium">
                    {scanConfig.threads || 25} threads
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="1" max="100"
                    value={scanConfig.threads || 25}
                    onChange={(e) => handleThreadsChange(e, 'scan')}
                    className="flex-1 h-2 bg-dark-400 rounded-lg appearance-none cursor-pointer accent-accent-blue hover:h-3 transition-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isDisabled || !!selectedPort} // Disable if port is selected
                  />
                  <span className="text-xs w-10 text-center bg-dark-500 py-1 rounded-lg">
                    {scanConfig.threads || 25}
                  </span>
                </div>
                <div className="flex justify-between text-xs mt-1 text-gray-400">
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                </div>
              </div>

              {/* Scan Timeout Input */}
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                <label className="block text-xs text-gray-400 mb-2">Scan Timeout (ms)</label>
                <div className="relative group">
                  <input
                    type="number" min="50" max="10000" step="50"
                    value={scanConfig.timeout || 250}
                    onChange={handleTimeoutChange}
                    className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-blue/50 hover:border-dark-300 transition-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isDisabled || !!selectedPort}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 group-hover:text-accent-blue transition-medium">
                    ms
                  </div>
                </div>
              </div>
            </>
          ) : (
            // --- Password Cracker Settings ---
            <>
              {/* Password Database Select */}
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                <label className="block text-xs text-gray-400 mb-2">Password Database</label>
                <select
                  className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  value={crackConfig.wordlist}
                  onChange={handleWordlistChange}
                  disabled={isDisabled || availableWordlists.length === 0}
                >
                  {availableWordlists.length === 0 && (
                    <option value="" disabled>Loading bases...</option>
                  )}
                  {availableWordlists.map(baseName => (
                    <option key={baseName} value={baseName}>
                      {baseName}
                    </option>
                  ))}
                  {/*<option value="manual">Manual Input</option>*/}
                </select>
                {availableWordlists.length === 0 && !isDisabled && (
                  <p className="text-xs text-accent-yellow mt-1">Waiting for password base list from server...</p>
                )}
              </div>

              {/* Manual Input Section */}
              {showManualInput && (
                <div className="bg-dark-500 rounded-lg p-4 border border-dark-400 animate-fade-in">
                  <div className="mb-3">
                    <label className="block text-xs text-gray-400 mb-1">RCON Port</label>
                    <input
                      type="number"
                      value={manualPort}
                      onChange={(e) => setManualPort(e.target.value)}
                      className="w-full bg-dark-600 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium"
                      placeholder="e.g., 25575"
                      min="1"
                      max="65535"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs text-gray-400 mb-1">Password (Optional)</label>
                    <input
                      type="password"
                      value={manualPassword}
                      onChange={(e) => setManualPassword(e.target.value)}
                      className="w-full bg-dark-600 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium"
                      placeholder="Leave empty to brute-force"
                    />
                  </div>
                  <button
                    onClick={handleManualSubmit}
                    disabled={!manualPort}
                    className={`w-full py-2 rounded-lg text-sm transition-medium ${
                      manualPort 
                        ? 'bg-accent-purple/20 text-accent-purple hover:bg-accent-purple/30'
                        : 'bg-dark-400 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <i className="fas fa-check mr-1"></i> Confirm Manual Input
                  </button>
                </div>
              )}

              {/* Crack Threads Range */}
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                <label className="block text-xs text-gray-400 mb-2 group">
                  Threads (Crack)
                  <span className="ml-2 text-accent-purple opacity-0 group-hover:opacity-100 transition-medium">
                    {crackConfig.threads || 25} threads
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="1" max="50"
                    value={crackConfig.threads || 25}
                    onChange={(e) => handleThreadsChange(e, 'crack')}
                    className="flex-1 h-2 bg-dark-400 rounded-lg appearance-none cursor-pointer accent-accent-purple hover:h-3 transition-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isDisabled}
                  />
                  <span className="text-xs w-10 text-center bg-dark-500 py-1 rounded-lg">
                    {crackConfig.threads || 25}
                  </span>
                </div>
                <div className="flex justify-between text-xs mt-1 text-gray-400">
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                </div>
              </div>

              {/* Crack Delay Range */}
              <div className="transform-gpu transition-medium hover:scale-[1.02]">
                <label className="block text-xs text-gray-400 mb-2 group">
                  Request Delay (Crack)
                  <span className="ml-2 text-accent-purple opacity-0 group-hover:opacity-100 transition-medium">
                    {crackConfig.delay || 150}ms
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="50" max="1000" step="50"
                    value={crackConfig.delay || 150}
                    onChange={handleDelayChange}
                    className="flex-1 h-2 bg-dark-400 rounded-lg appearance-none cursor-pointer accent-accent-purple hover:h-3 transition-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isDisabled}
                  />
                  <span className="text-xs w-12 text-center bg-dark-500 py-1 rounded-lg">
                    {crackConfig.delay || 150}ms
                  </span>
                </div>
                <div className="flex justify-between text-xs mt-1 text-gray-400">
                  <span>Fast</span>
                  <span>Balanced</span>
                  <span>Stealthy</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Single Start/Status Button */}
        <button
          onClick={onStartProcess}
          disabled={!canProcess}
          className={`w-full mt-6 py-3 rounded-xl text-white transition-medium font-medium relative z-10 flex items-center justify-center group disabled:cursor-not-allowed ${
              isProcessing
                  ? 'bg-gray-500 cursor-wait animate-pulse'
                  : isHacked
                  ? 'bg-gradient-to-r from-accent-green to-accent-blue cursor-not-allowed'
                  : canProcess
                  ? selectedPort
                     ? 'bg-gradient-to-r from-accent-pink to-accent-purple hover:shadow-glow-pink active:translate-y-0.5 transform-gpu'
                     : 'bg-gradient-to-r from-accent-blue to-accent-green hover:shadow-glow active:translate-y-0.5 transform-gpu'
                  : 'bg-dark-400 text-gray-500'
          }`}
        >
          <i className={`fas ${getButtonIcon()} mr-2 ${isProcessing ? '' : 'group-hover:rotate-12'} transition-transform`}></i>
          {getButtonText()}
        </button>

        {/* Add tooltip/message if button is disabled because wordlist is missing */}
        {!crackConfig.wordlist && selectedPort && !isProcessing && !isHacked && (
          <p className="text-center text-xs text-accent-yellow mt-2">Select a password database</p>
        )}
      </div>
    </div>
  );
};

export default ConfigurationPanel;